<template>
    <div class="coach-profile-page">

        <div v-if="loading" class="profile-loading">
            <skeleton-loader type="coach-profile" />
        </div>

        <div v-else-if="!coach" class="empty-state">
            <v-icon size="34" color="txtMuted">mdi-account-off-outline</v-icon>
            <div class="empty-title">{{ $t('coach.coach_not_found') }}</div>
            <back-button class="mt-4" />
        </div>

        <template v-else>
            <!-- BACK -->
            <div class="profile-back">
                <back-button />
            </div>

            <div class="profile-content">
                <!-- HERO -->
                <div class="profile-hero">
                    <div v-if="coach.avatar" class="profile-photo"
                        :style="{ backgroundImage: `url('${coach.avatar}')` }" />
                    <div v-else class="profile-photo profile-photo-initials">{{ initials }}</div>
                    <div class="profile-accent-bar" />
                </div>

                <!-- INFO -->
                <div class="profile-info">
                    <div class="profile-name">{{ coach.name }}</div>
                    <div v-if="coach.description" class="profile-bio">{{ coach.description }}</div>
                    <div v-if="coachSports" class="profile-sports">{{ coachSports }}</div>
                </div>

                <!-- ACCORDIONS -->
                <expanded-sections :sections="sections" :value="0">
                    <template #info>
                        <div v-if="coach.experience" class="info-experience">
                            {{ coach.experience }} {{ $t('coach.experience_years') }}
                        </div>
                        <div class="info-text">{{ coach.professional_preparation }}</div>
                    </template>

                    <template #students>
                        <div class="trained-grid">
                            <div v-for="student in lastStudents" :key="student.id" class="trained-card">
                                <student-avatar :avatar="student.avatar" :name="student.name" :avatar-key="student.id"
                                    :size="56" :radius="0" class="trained-card-img" />
                                <div class="trained-card-name">
                                    {{ student.name || $t('common.not_available_short') }}
                                </div>
                                <div class="trained-card-sport">
                                    {{ sportLabel(student.sportSelected) || $t('common.not_available_short') }}
                                </div>
                            </div>
                        </div>
                    </template>
                </expanded-sections>

                <!-- ACTIONS -->
                <div class="profile-actions">
                    <v-btn class="message-cta" depressed @click="showChat">
                        <v-icon size="16" left>mdi-message-processing-outline</v-icon>
                        {{ $t('coach.send_message') }}
                    </v-btn>
                </div>
            </div>

            <chat-dialog :clientId="coachId" ref="chat" />
        </template>

    </div>
</template>

<script>

import ChatDialog from '@/components/student/ChatDialog'

import { db } from '@/utils/Firebase'
import { getInitials } from '@/utils/Common'
import { mapMutations } from 'vuex'
import BackButton from "@/components/common/BackButton";
import StudentAvatar from "@/components/common/StudentAvatar";
import ExpandedSections from "@/components/common/ExpandedSections";
import SkeletonLoader from "@/components/common/SkeletonLoader";

const MAX_TRAINED_STUDENTS = 6

export default {
    components: {
        SkeletonLoader,
        ExpandedSections,
        StudentAvatar,
        BackButton,
        ChatDialog
    },
    data() {
        return {
            coach: null,
            loading: false,
            lastStudents: [],
        }
    },
    computed: {
        coachId(vm = this) {
            return vm.$route.params.uid
        },
        initials(vm = this) {
            return getInitials(vm.coach?.name) || '-'
        },
        coachSports(vm = this) {
            return (vm.coach?.sport_areas || []).map(sport => vm.sportLabel(sport)).join(', ')
        },
        trainedStudents(vm = this) {
            const total = vm.coach?.trainedStudents || 0
            return total < 10 ? `0${total}` : `${total}`
        },
        hasInfo(vm = this) {
            return !!(vm.coach?.experience || vm.coach?.professional_preparation)
        },
        sections(vm = this) {
            const sections = []
            if (vm.hasInfo) sections.push({ title: vm.$t('coach.professional_info'), value: 'info' })
            if (vm.lastStudents.length > 0) {
                sections.push({
                    title: vm.$t('coach.trained_with'),
                    value: 'students',
                    count: vm.trainedStudents
                })
            }
            return sections
        },
    },
    async mounted() {
        const vm = this
        await vm.loadCoach()
    },
    destroyed() {
        this.setToolbarCoach()
    },
    watch: {
        coachId() {
            this.loadCoach()
        }
    },
    methods: {
        ...mapMutations(['setToolbarCoach']),
        showChat() {
            const vm = this
            vm.$refs.chat.show()
        },
        async loadCoach() {
            const vm = this
            await vm.withLoading(async () => {
                vm.lastStudents = []
                let coachRef = await db.collection('users').doc(vm.coachId).get()
                if (!coachRef.exists) {
                    vm.coach = null
                    return vm.setToolbarCoach()
                }
                vm.coach = coachRef.data()
                vm.setToolbarCoach({ id: coachRef.id, ...vm.coach })

                const studentIds = (vm.coach?.lastStudents || []).slice(0, MAX_TRAINED_STUDENTS)
                if (studentIds.length === 0) return
                const refs = await Promise.all(studentIds.map(id => db.collection('users').doc(id).get()))
                vm.lastStudents = refs.filter(ref => ref.exists).map(ref => ({ id: ref.id, ...ref.data() }))
            })
        }
    },
}
</script>

<style scoped>

.coach-profile-page {
    padding-bottom: 40px;
}

.profile-content {
    max-width: 720px;
    margin: 0 auto;
}

.profile-loading,
.empty-state {
    padding: 24px 20px;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 60px;
    text-align: center;
}

.empty-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
    margin-top: 8px;
}

.profile-back {
    background: var(--v-bg-base);
    padding: 16px 20px;
}

.profile-hero {
    position: relative;
    display: flex;
    justify-content: center;
    padding: 20px 20px 24px;
    background: var(--v-surface-base);
}

.profile-photo {
    width: 140px;
    height: 140px;
    border-radius: 12px;
    border: 2px solid var(--v-outline2-base);
    background-color: var(--v-surface2-base);
    background-size: cover;
    background-position: 50% 50%;
}

.profile-photo-initials {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 36px;
    font-weight: 800;
    color: #0C0E12;
    background: linear-gradient(135deg, #CCFF33 0%, #339999 100%);
}

.profile-accent-bar {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(135deg, #CCFF33 0%, #339999 100%);
}

.profile-info {
    background: var(--v-surface-base);
    border-bottom: 1px solid var(--v-outline-base);
    padding: 20px;
    text-align: center;
}

.profile-name {
    font-size: 22px;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: var(--v-txtPrimary-base);
}

.profile-bio {
    font-size: 13px;
    color: var(--v-txtSecondary-base);
    margin-top: 4px;
    line-height: 1.5;
}

.profile-sports {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    margin-top: 6px;
}

.info-experience {
    font-size: 13px;
    font-weight: 700;
    color: var(--v-accent-base);
    margin-bottom: 6px;
}

.theme--dark .info-experience {
    color: var(--v-secondary-base);
}

.info-text {
    font-size: 13px;
    color: var(--v-txtSecondary-base);
    line-height: 1.7;
    white-space: pre-line;
}

.trained-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 10px;
    padding: 4px 0;
}

.trained-card {
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    overflow: hidden;
}

.trained-card-img {
    width: 100% !important;
    height: 56px !important;
}

.trained-card-name {
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    padding: 7px 8px 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.trained-card-sport {
    font-size: 10px;
    color: var(--v-txtMuted-base);
    padding: 0 8px 7px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.profile-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 20px;
}

.message-cta {
    height: 48px !important;
    border-radius: 24px;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: none;
    padding: 0 20px !important;
}

.message-cta {
    background: var(--v-primary-base) !important;
    color: #0C0E12 !important;
    box-shadow: 0 4px 16px rgba(204, 255, 51, 0.25) !important;
}

.theme--light .message-cta {
    background: var(--v-accent-base) !important;
    color: #FFFFFF !important;
    box-shadow: 0 4px 16px rgba(51, 153, 153, 0.25) !important;
}

@media (max-width: 959px) {
    .coach-profile-page {
        padding-bottom: 96px;
    }

    .profile-actions {
        position: fixed;
        right: 16px;
        bottom: calc(72px + var(--safe-area-inset-bottom, 0px));
        z-index: 6;
        padding: 0;
    }
}

</style>
